/**
 * Places: the asset the whole product is built on.
 *
 * The category and best-time vocabularies below are copied from `db/migrations/001_init.sql` rather
 * than restated. They are Postgres enums and CHECK constraints there, so a value outside these sets
 * is rejected by the database; declaring a different set here would move the failure from the
 * request boundary to a 500, and the client message would lose its field name.
 *
 * A note on the `null`s scattered through this file: they are the contract form of the product's
 * central rule (spec P5, API contract §1.2). Coordinates, ratings and opening hours are `null` when
 * unknown and MUST NOT be rendered as zero or as an estimate. The distinction is not pedantry —
 * `distance_m: 0` and `distance_m: null` are different facts, and only one of them means "these two
 * stops are the same place".
 */

import { z } from "zod";

import { Area, GeoPoint, Id, IsoDateTime, pageOf } from "./common.ts";

/**
 * What kind of place this is. A set, not a single value: many real places are honestly several
 * things at once, and forcing one choice made the user pick the least-wrong one and lose the rest.
 *
 * Empty means "not recorded". That is distinct from `other`, which means the user looked and found
 * nothing that fits — hence `subcategory` being required when `other` is present.
 */
export const PlaceCategory = z.enum([
  "cafe",
  "restaurant",
  "dessert",
  "market",
  "attraction",
  "nature",
  "stay",
  "transport",
  "shopping",
  "other",
]).meta({
  id: "PlaceCategory",
  description: "A category is a member of a set; `other` requires `subcategory` to be answered.",
});

export const PlaceCategories = z.array(PlaceCategory)
  .refine((c) => new Set(c).size === c.length, {
    message: "Một loại không được lặp lại.",
  })
  .meta({
    id: "PlaceCategories",
    description:
      "The categories a place belongs to. Empty means not recorded, which is distinct from `other`. " +
      "Members must be distinct; `other` additionally requires `subcategory`, enforced on the request.",
  });

/**
 * When a place is worth visiting.
 *
 * `cả ngày` is mutually exclusive with the rest, and this is enforced in the database as well
 * (`places_best_time_all_day_exclusive`). A place that suits the whole day, and is also listed as a
 * morning place, is a contradiction: the second fact adds nothing and makes "which of my places suit
 * a morning?" return a place the user already said is not morning-specific.
 *
 * The values are Vietnamese because they are the user's own words; the client displays them
 * verbatim rather than mapping through a translation table.
 */
export const BestTimeSlot = z.enum(["sáng", "trưa", "chiều-tối", "cả ngày"]).meta({
  id: "BestTimeSlot",
  description: "Label shown to the user as-is. `cả ngày` excludes the others.",
});

export const BestTime = z.array(BestTimeSlot)
  .refine((slots) => new Set(slots).size === slots.length, { message: "Một buổi không được lặp lại." })
  .refine((slots) => !(slots.includes("cả ngày") && slots.length > 1), {
    message: 'Chọn "cả ngày" thì không chọn thêm buổi khác.',
  })
  .meta({ id: "BestTime", description: "Time slots this place suits. Empty means not recorded." });

/**
 * Where a place came from.
 *
 * The MVP does not fetch a source URL — not to extract a title, not to check it is reachable
 * (ADR-004). `url` is stored as the user's reference and returned so they can find the original post
 * again. This is why the schema is a plain `url` with no derived fields: there is nothing derived.
 *
 * The provider enum mirrors `source_kind` in the database. `text` exists for a paste that is not a
 * link — a note someone copied out of a chat — because forcing that into `url` would mean storing
 * something that is not one.
 */
export const SourceKind = z.enum(["manual", "screenshot", "text", "url", "provider_search"]).meta({
  id: "SourceKind",
  description: "How the place reached the system. Mirrors the `source_kind` database enum.",
});

/** A source as supplied by a client. No `id`: the server assigns it. */
export const SourceInput = z.object({
  kind: SourceKind,
  provider: z.string().nullable().optional().describe("Set for `provider_search`, e.g. `osm`."),
  url: z.url().nullable().optional().describe("Stored, never fetched. Null for a manual entry."),
  attribution: z.string().nullable().optional().describe("Vietnamese credit line, e.g. 'Xem bài gốc trên TikTok'."),
}).meta({ id: "SourceInput", description: "A source as submitted by a client." });

/** A source as returned. Adds the server-assigned `id` and any snippet the user saved. */
export const Source = z.object({
  id: Id,
  kind: SourceKind,
  provider: z.string().nullable(),
  url: z.string().nullable(),
  attribution: z.string().nullable(),
  snippet: z.string().nullable(),
}).meta({ id: "Source", description: "A stored reference to where a place came from (AC-5.2)." });

/**
 * The four suitability flags.
 *
 * Each is a nullable boolean, and the null is load-bearing: `false` means the user said this place
 * does not suit children, and `null` means they did not say. Collapsing null to false would turn
 * every unanswered question into a negative answer, and the filter that reads these would then
 * exclude places the user simply had not got to yet.
 */
const suitability = {
  is_indoor: z.boolean().nullable().describe("Null means not recorded, which is not the same as false."),
  kids_friendly: z.boolean().nullable(),
  elderly_friendly: z.boolean().nullable(),
  pet_friendly: z.boolean().nullable(),
};

/**
 * A place as it appears in a list.
 *
 * Deliberately not the full card: a list of 100 places with every field would be a payload the
 * manage screen never reads, and this endpoint is loaded on a phone over a Vietnamese mobile
 * connection.
 */
export const PlaceSummary = z.object({
  id: Id,
  name: z.string().min(1),
  categories: PlaceCategories,
  subcategory: z.string().nullable().describe("What `other` actually was (AC-3.12)."),
  address: z.string().nullable(),
  areas: z.array(Area),
  best_time: BestTime,
  ...suitability,
  location: GeoPoint.nullable().describe("Null when never geocoded. Rendered as 'chưa có toạ độ', never guessed."),
  /**
   * The primary source: where this place was saved from.
   *
   * Singular here because the list only ever renders one line of provenance. On the full card this
   * is the first entry of `sources`, and the two are derived from the same rows — a client may read
   * either, and a client that sees them disagree has found a server bug rather than a design choice.
   */
  source: Source.nullable(),
  created_at: IsoDateTime,
}).meta({ id: "PlaceSummary", description: "A place in a list, without the full card." });

export const PlaceSummaryPage = pageOf(PlaceSummary, "PlaceSummaryPage", "The caller's saved places.");

/**
 * `GET /places` query.
 *
 * `q` searches name, address, area names and the note, **folded** — so `cau dat` finds a place in
 * `Cầu Đất`. That folding is the whole reason PostgreSQL was chosen over a document store
 * (ADR-001): it is a generated column plus `pg_trgm`, in one place, that every query gets.
 */
export const PlaceListQuery = z.object({
  q: z.string().optional().describe("Folded match over name, address, area names and note (AC-2.2)."),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  cursor: z.string().optional(),
  area_id: Id.optional(),
  category: PlaceCategory.optional(),
  has_location: z
    .enum(["true", "false"])
    .optional()
    .describe(
      "`false` returns the places with no coordinate yet — the ones that cannot enter a plan, which " +
        "is the list a user fixes before planning a day.",
    ),
}).meta({ id: "PlaceListQuery", description: "Query for the manage list." });

/**
 * `GET /places/map-link` — what a Google Maps link points at.
 *
 * Its own endpoint rather than a parameter on `/places/lookup`, because it answers a different question
 * with a different failure model. `lookupPlace` asks a geocoder for candidates by *name* and can return
 * several plausible answers with confidence scores; this resolves *one* link to *one* coordinate Google
 * published, and there is nothing to rank or refuse. Folding it in would give `/places/lookup` two modes
 * whose parameters do not overlap — the same mistake the search endpoint's documentation rejects a
 * `mode` parameter for.
 *
 * `status` is four states, not a boolean, for the reason `provider_status` has three: "this link carries
 * no coordinate" and "we could not reach Google" are different sentences, only one of them is the user's
 * to act on, and only one is worth retrying.
 */
export const ResolveMapLinkStatus = z.enum([
  "ok",
  /** The URL is not a Google Maps link at all, so nothing here could read it. */
  "not_a_map_link",
  /** Google answered, and the link carries no coordinate — a link to a name search, for instance. */
  "no_coordinate",
  /**
   * Google answered **404**: the link is dead.
   *
   * Its own state, and the reason is measured. An unknown `maps.app.goo.gl` code returns 404, and the
   * first version of the resolver threw on any 4xx, so a dead link reached the user as "Google Maps is
   * unavailable right now" — advice to retry something that can never work. `no_coordinate` is wrong for
   * it too: that means the link resolved to a page with no pin.
   */
  "link_not_found",
  /** Google could not be reached. Retryable, and the pin can be placed by hand meanwhile. */
  "unavailable",
]).meta({
  id: "ResolveMapLinkStatus",
  description: "Why a map link did or did not produce a coordinate. `no_coordinate` is not `unavailable`.",
});

export const ResolveMapLinkResponse = z.object({
  status: ResolveMapLinkStatus,
  /** The coordinate Google published, or null when the status is not `ok`. */
  location: GeoPoint.nullable(),
  /** The place's name from the URL path, when it carried one. Never invented. */
  name: z.string().nullable(),
  /**
   * The URL Google redirected to, so the caller can store it as the place's own source.
   *
   * Returned rather than hidden because the user did give us this link: recording it as provenance is
   * what lets them find the post or listing again, and it is the long form — the short link they pasted
   * would expire into nothing useful.
   */
  resolved_url: z.string().nullable(),
}).meta({
  id: "ResolveMapLinkResponse",
  description: "What a Google Maps link points at, with the reason attached when it gave nothing.",
});

/**
 * The full place card.
 *
 * Separate from `PlaceSummary` rather than an extension of it, because the two are read by different
 * screens with different needs, and a client that had to handle the union would have to know which
 * fields are present in which context. Two shapes with a shared core is easier to reason about than
 * one shape that is sometimes half-filled.
 */
export const Place = PlaceSummary.extend({
  description: z.string().nullable(),
  opening_hours: z.object({
    open: z.string(),
    close: z.string(),
  }).nullable(),
  price_range: z.int().min(1).max(4).nullable().describe("1 to 4. Null when unknown."),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  estimated_duration_min: z.int().positive().nullable()
    .describe("How long a visit takes. Null when unknown; the planner then uses a per-category default and says so."),
  amenities: z.array(z.string()),
  /**
   * Every source, in the order they were saved.
   *
   * Plural, and superseding the summary's single `source`, which is the first of these. The detail
   * screen shows all of them because a place reached by two posts is a place two people recommended,
   * and that is worth seeing (AC-5.2).
   */
  sources: z.array(Source),
  updated_at: IsoDateTime,
}).meta({ id: "Place", description: "The full place card (AC-2.9)." });

// ------------------------------------------------------------------------------- provider lookup

/**
 * A provider result, as returned by `GET /places/lookup` (AC-3.4).
 *
 * `match_quality` is what lets the client refuse a bad answer rather than store it. It is measured
 * on folded names: `Nook Cafe` in Đà Lạt exists in Google Maps and nowhere in OSM, and the provider
 * answers with the city, 4 km off. A plausible-looking wrong coordinate is worse than no coordinate,
 * because it silently reorders a plan (measured: 1 200 m is enough to change the order, and 2 500 m
 * makes the route 7.5 km worse).
 */
export const PlaceLookupResult = z.object({
  name: z.string(),
  address: z.string().nullable(),
  location: GeoPoint.nullable(),
  provider: z.string().describe("Which upstream answered: `nominatim` for a name lookup, `overpass` for a tag browse."),
  provider_place_id: z.string().nullable()
    .describe("Stable provider identity, e.g. `node/4502225092`. Used for dedup, never shown."),
  match_quality: z.number().min(0).max(1)
    .describe("0 to 1. Low means the provider did not really recognise the name; the client should not offer it."),
  /**
   * The application's own categories, translated from the provider's tags.
   *
   * Present so a result can be saved without the user re-answering a question the provider already
   * answered: a browse for cafés returns cafés, and the capture form's category chips are filled in
   * when the candidate is adopted. Empty is a real answer — the tags mapped to nothing — and is not
   * `other`, which means the user looked and found no fitting category.
   *
   * Used **bare**, with no `.describe()` on the field, and that is load-bearing rather than tidy.
   * Measured: `.describe()` on this schema returns a new instance with no `id`, and `.meta()` replaces
   * metadata instead of merging — so either call would leave an unnamed array carrying a `.refine()`,
   * and the uniqueness constraint would be dropped from the generated document without a murmur. The
   * schema's own description already says what empty means; `contract.test.ts` is what refuses the
   * alternative.
   */
  categories: PlaceCategories,
  /**
   * Metres from the centre the search was ranked around.
   *
   * Null when the search had no centre. Never estimated: the distance the user reads in a list is
   * either measured from a point they chose or absent.
   */
  distance_m: z.number().nonnegative().nullable()
    .describe("Null when the search had no centre. Shown in the list; never guessed."),
  /**
   * The provider's own plus code, when it publishes one.
   *
   * Google and Goong do; a Nominatim hit has none, so `null` is the ordinary value from the OSM path
   * rather than a failure. Carried because it is a coordinate a person can read aloud to somebody
   * standing in the street — useful precisely where a lat/lon is not. Never computed from `location`: a
   * derived plus code would be a second position nothing verified.
   */
  plus_code: z.string().nullable()
    .describe("The provider's plus code, or null when its answer did not carry one. Never derived."),
}).meta({ id: "PlaceLookupResult", description: "A provider place, with the confidence needed to refuse it." });

/**
 * Whether the provider half of a response is usable.
 *
 * Three states rather than a boolean, because the client's message differs: `unavailable` means the
 * service is down and the user should retry, `quota_exceeded` means the app has hit a free-tier
 * ceiling and will recover later. Both are distinct from `ok` with zero results, where the honest
 * message is "not found".
 */
export const ProviderStatus = z.enum(["ok", "unavailable", "quota_exceeded"]).meta({
  id: "ProviderStatus",
  description: "State of the upstream provider, so an empty list is never mistaken for 'not found'.",
});

export const PlaceLookupResponse = z.object({
  results: z.array(PlaceLookupResult),
  status: ProviderStatus,
}).meta({ id: "PlaceLookupResponse", description: "Provider-only lookup. Makes no claim about the user's own places." });

/**
 * Request for Gemini AI-grounded place search.
 */
export const AiPlaceLookupRequest = z.object({
  query: z.string().min(1).describe("Search query, venue name, address, or review snippet"),
  area_hint: z.string().optional().describe("Optional area/city hint e.g. Đà Lạt, TP.HCM"),
  api_key: z.string().optional().describe("Optional user BYOK key for Gemini"),
}).meta({ id: "AiPlaceLookupRequest", description: "Query for AI-grounded place search with Gemini." });

/**
 * Result from Gemini AI place search grounded with Google Search.
 *
 * There is deliberately no `confidence`. The model's own 0–1 self-report is not a measurement, so a
 * client that gated on it would be trusting a number nothing verified — and the first version made it
 * worse by substituting 0.9 when the model omitted it, so an unverified answer arrived looking like a
 * strong one. The fields a caller can act on are the place's own facts.
 */
export const AiPlaceLookupResult = z.object({
  name: z.string(),
  address: z.string().nullable(),
  /**
   * The coordinate — and only when a Google Maps link in the model's answer carried it.
   *
   * Never the model's estimate: the prompt does not ask for a position and one it volunteers anyway is
   * discarded, so a non-null value is a link-verified one and `null` is the ordinary answer for a
   * name-only result. The capture form's map-pin offer is the honest path when this is null.
   */
  location: GeoPoint.nullable().describe(
    "Present only when a cited Google Maps link carried it; never model-estimated. Null otherwise.",
  ),
  google_maps_url: z.string().nullable().describe("A Google Maps link the model cited, when it gave one."),
  categories: PlaceCategories,
  best_time: BestTime,
  notes: z.string().nullable(),
  sources: z.array(z.string()).optional(),
}).meta({
  id: "AiPlaceLookupResult",
  description: "A Gemini place result. Coordinates come only from a cited Google Maps link, never the model.",
});

/**
 * Response for Gemini AI place search.
 */
export const AiPlaceLookupResponse = z.object({
  results: z.array(AiPlaceLookupResult),
  status: ProviderStatus,
  error_message: z.string().nullable().optional(),
}).meta({
  id: "AiPlaceLookupResponse",
  description: "AI place lookup response. `location` is verified from a Google Maps link or null.",
});

/**
 * `other` requires `subcategory`.
 *
 * Cross-field, so it lives on the request rather than on `PlaceCategories`: a category set is valid
 * in isolation, and what records nothing is the combination with a missing `subcategory` (AC-3.12).
 * Enforced here as well as described in `json-schema.ts`, so the document and the server state the
 * same rule instead of the document merely asserting one.
 */
function requireSubcategoryForOther(
  request: { categories?: readonly string[] | undefined; subcategory?: string | null | undefined },
  ctx: z.RefinementCtx,
): void {
  if (request.categories?.includes("other") !== true) return;
  if (typeof request.subcategory === "string" && request.subcategory.length > 0) return;
  ctx.addIssue({
    code: "custom",
    path: ["subcategory"],
    message: 'Chọn "Khác" thì cần ghi rõ là loại gì.',
  });
}

// ------------------------------------------------------------------------------------- mutations

/**
 * `POST /places` — the capture path.
 *
 * Only `name` is required. Everything else may be added later, and that is deliberate rather than
 * lazy: the main risk of the user-authored capture design (ADR-004) is that a six-field form costs
 * more than saving the place is worth, so the price of saving one place has to be one field
 * (AC-3.1).
 */
export const CreatePlaceRequest = z.object({
  name: z.string().min(1).describe("The only required field."),
  categories: PlaceCategories.optional(),
  subcategory: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  location: GeoPoint.nullable().optional(),
  best_time: BestTime.optional(),
  is_indoor: z.boolean().nullable().optional(),
  kids_friendly: z.boolean().nullable().optional(),
  elderly_friendly: z.boolean().nullable().optional(),
  pet_friendly: z.boolean().nullable().optional(),
  description: z.string().nullable().optional(),
  area_ids: z.array(Id).optional().describe("The user's own areas. An unknown id is rejected, not created."),
  sources: z.array(SourceInput).optional(),
  confirm_duplicate: z.boolean().default(false)
    /*
     * Accepted, and gates nothing.
     *
     * `POST /places` writes what it is given. The warning lives in `POST /places/duplicates`, which reads
     * the same SQL function the merge uses, so the warning and any later check cannot disagree.
     *
     * A server-side refusal was considered and deliberately not built. It would need either a second 409
     * body shape — `PROBLEM_BY_STATUS` gives every status exactly one, so every route declaring 409 would
     * inherit a field it never sets — or nothing at all, because the client has already run the check and
     * holds the candidates. It is not refused for the second reason: the check is advisory by decision,
     * and the cost is that two devices can still save the same place twice.
     */
    .describe(
      "Accepted but gates nothing: a likely duplicate does not refuse the write. Ask POST /places/duplicates before saving to warn.",
    ),
}).superRefine(requireSubcategoryForOther)
  .meta({ id: "CreatePlaceRequest", description: "POST /places. Only `name` is required (AC-3.1)." });

/** The `201` body. */
export const CreatePlaceResponse = z.object({
  place: Place,
}).meta({ id: "CreatePlaceResponse", description: "201 from POST /places." });

/** `PATCH /places/:id` — what the edit form sends. Omitted fields are left unchanged. */
export const UpdatePlaceRequest = z.object({
  name: z.string().min(1).optional(),
  categories: PlaceCategories.optional(),
  subcategory: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  location: GeoPoint.nullable().optional(),
  best_time: BestTime.optional(),
  is_indoor: z.boolean().nullable().optional(),
  kids_friendly: z.boolean().nullable().optional(),
  elderly_friendly: z.boolean().nullable().optional(),
  pet_friendly: z.boolean().nullable().optional(),
  description: z.string().nullable().optional(),
  area_ids: z.array(Id).optional(),
  /**
   * Sources to **add**. Omitted leaves the stored provenance alone, which is what lets an edit that
   * touches only a phone number keep the link the place was captured from.
   *
   * Additive rather than a replacement, and the distinction is deliberate: the edit form knows only
   * the link the user just pasted, not the place's whole provenance, so a replace would delete a
   * source the form never showed. Removing a source is a separate operation with no field here.
   */
  sources: z.array(SourceInput).optional(),
}).superRefine(requireSubcategoryForOther)
  .meta({ id: "UpdatePlaceRequest", description: "PATCH /places/:id." });

/**
 * `DELETE /places/:id` reports what else it touched.
 *
 * `orphaned_areas` lets the client prune its local area vocabulary without a second request. The
 * server does not delete them itself: an area is the user's vocabulary, and a concurrent edit on
 * another device might still be using one (AC-2.9d).
 */
export const DeletePlaceResponse = z.object({
  deleted: Id,
  affected_itinerary_items: z.int().gte(0)
    .describe("Plans containing this place. Non-zero means the user should be warned before confirming."),
  orphaned_areas: z.array(z.string()).describe("Areas no remaining place uses. The client may prune these."),
}).meta({ id: "DeletePlaceResponse", description: "200 from DELETE /places/:id." });

// ---------------------------------------------------------------------------------- duplicates

/**
 * A place the caller already saved that a draft looks like.
 *
 * The shape is `PlacesRepository.findDuplicates`' own, unchanged: `location` is required because the
 * database rule is proximity AND name, so a candidate without a coordinate cannot be one, and `score`
 * is the similarity `find_duplicate_places` computed — the greater of the raw and the core-name
 * comparison. It is returned rather than reduced to a boolean so the client can say how sure it is.
 */
export const DuplicateCandidate = z.object({
  id: Id,
  name: z.string(),
  location: GeoPoint,
  score: z.number().min(0).max(1)
    .describe("The database's similarity for this pair. 1 means the folded names are identical."),
}).meta({
  id: "DuplicateCandidate",
  description: "A place the caller already saved that a draft may duplicate (AC-3.5).",
});

/**
 * `POST /places/duplicates` — the pre-save check on a draft.
 *
 * **POST, not the `GET` the route table once named.** The body is a *draft place*, not an addressable
 * resource: the name is not saved yet and has no identity to be fetched by, and a query string would
 * write a Vietnamese place name into every access log and proxy cache on the way. The check is a read
 * in intent, and POST is the honest verb for a transient body — which is also what AC-3.5 names.
 */
export const DuplicateCheckRequest = z.object({
  name: z.string().min(1).max(200),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
}).meta({
  id: "DuplicateCheckRequest",
  description: "POST /places/duplicates. The draft the user is about to save.",
});

/** Empty means a definite no: the rule ran and matched nothing among the caller's own places. */
export const DuplicateCheckResponse = z.object({
  duplicates: z.array(DuplicateCandidate),
}).meta({ id: "DuplicateCheckResponse", description: "The caller's own places the draft may duplicate." });

/**
 * Two saved places the rule holds, with the earlier-created one as `keep`.
 *
 * The direction is the server's answer, not the client's guess: a merge is a write, and a client that
 * picked the other way would move a place's history into a row it did not expect.
 */
export const DuplicatePair = z.object({
  keep: DuplicateCandidate,
  drop: DuplicateCandidate,
}).meta({ id: "DuplicatePair", description: "One likely duplicate pair among the caller's saved places." });

/** `GET /places/duplicates` — the pairs that already exist, for the review surface. */
export const DuplicatePairList = z.object({
  pairs: z.array(DuplicatePair),
}).meta({ id: "DuplicatePairList", description: "GET /places/duplicates. Pairs already saved." });

/** `POST /places/:id/merge` — the user's explicit merge decision (AC-3.5). */
export const MergePlaceRequest = z.object({
  duplicate_place_id: Id.describe("The place to fold into `:id`. It survives no longer: it is soft-deleted."),
}).meta({ id: "MergePlaceRequest", description: "POST /places/:id/merge." });

export const MergePlaceResponse = z.object({
  place: Place,
}).meta({ id: "MergePlaceResponse", description: "200 from POST /places/:id/merge. The surviving place." });

export type Place = z.infer<typeof Place>;
export type PlaceSummary = z.infer<typeof PlaceSummary>;
export type PlaceCategory = z.infer<typeof PlaceCategory>;
export type BestTimeSlot = z.infer<typeof BestTimeSlot>;
export type Source = z.infer<typeof Source>;
export type SourceInput = z.infer<typeof SourceInput>;
export type PlaceLookupResult = z.infer<typeof PlaceLookupResult>;
export type ProviderStatus = z.infer<typeof ProviderStatus>;
export type CreatePlaceRequest = z.infer<typeof CreatePlaceRequest>;
export type CreatePlaceResponse = z.infer<typeof CreatePlaceResponse>;
export type UpdatePlaceRequest = z.infer<typeof UpdatePlaceRequest>;
export type DeletePlaceResponse = z.infer<typeof DeletePlaceResponse>;
export type DuplicateCandidate = z.infer<typeof DuplicateCandidate>;
export type DuplicateCheckRequest = z.infer<typeof DuplicateCheckRequest>;
export type DuplicateCheckResponse = z.infer<typeof DuplicateCheckResponse>;
export type DuplicatePair = z.infer<typeof DuplicatePair>;
export type DuplicatePairList = z.infer<typeof DuplicatePairList>;
export type MergePlaceRequest = z.infer<typeof MergePlaceRequest>;
export type MergePlaceResponse = z.infer<typeof MergePlaceResponse>;
export type ResolveMapLinkResponse = z.infer<typeof ResolveMapLinkResponse>;
export type AiPlaceLookupRequest = z.infer<typeof AiPlaceLookupRequest>;
export type AiPlaceLookupResult = z.infer<typeof AiPlaceLookupResult>;
export type AiPlaceLookupResponse = z.infer<typeof AiPlaceLookupResponse>;
